import React, { useState, useEffect, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface FadeTransitionProps {
  children: ReactNode;
  className?: string;
  duration?: number;
  delay?: number;
}

/**
 * FadeTransition component that animates children with a fade effect
 */
export const FadeTransition: React.FC<FadeTransitionProps> = ({
  children,
  className = '',
  duration = 0.5,
  delay = 0,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return (
    <AnimatePresence mode='wait'>
      {mounted && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration, delay }}
          className={className}>
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface SlideTransitionProps {
  children: ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  className?: string;
  duration?: number;
  delay?: number;
  distance?: number;
}

/**
 * SlideTransition component that animates children with a slide effect
 */
export const SlideTransition: React.FC<SlideTransitionProps> = ({
  children,
  direction = 'up',
  className = '',
  duration = 0.5,
  delay = 0,
  distance = 20,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const directionMap = {
    up: { y: distance },
    down: { y: -distance },
    left: { x: distance },
    right: { x: -distance },
  };

  const initial = { ...directionMap[direction], opacity: 0 };
  const animate = direction.includes('y') ? { y: 0, opacity: 1 } : { x: 0, opacity: 1 };

  return (
    <AnimatePresence mode='wait'>
      {mounted && (
        <motion.div
          initial={initial}
          animate={animate}
          exit={{ ...initial, opacity: 0 }}
          transition={{ duration, delay, ease: 'easeOut' }}
          className={className}>
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface ScaleTransitionProps {
  children: ReactNode;
  className?: string;
  duration?: number;
  delay?: number;
  initialScale?: number;
}

/**
 * ScaleTransition component that animates children with a scale effect
 */
export const ScaleTransition: React.FC<ScaleTransitionProps> = ({
  children,
  className = '',
  duration = 0.5,
  delay = 0,
  initialScale = 0.95,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return (
    <AnimatePresence mode='wait'>
      {mounted && (
        <motion.div
          initial={{ opacity: 0, scale: initialScale }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: initialScale }}
          transition={{ duration, delay, ease: 'easeOut' }}
          className={className}>
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * AnimatedPage component provides a consistent page transition effect
 */
export const AnimatedPage: React.FC<{ children: ReactNode }> = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 20 }}
    transition={{ duration: 0.4 }}>
    {children}
  </motion.div>
);
